import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import { LogoMark } from "../components/Logo";
import { toast } from "sonner";
import { KeyRound, Lock, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const { resetPassword, forgotPassword } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { toast.error("Le password non coincidono"); return; }
    if (pwd.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    if (code.length !== 6) { toast.error("Codice di 6 cifre richiesto"); return; }
    setLoading(true);
    try {
      const u = await resetPassword(email, code, pwd);
      toast.success("Password reimpostata con successo!");
      nav(u?.role === "employee" ? "/portal" : "/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore reset");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (!email) { toast.error("Inserisci l'email"); return; }
    try {
      await forgotPassword(email);
      toast.success("Nuovo codice inviato");
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center px-6 py-10 relative overflow-hidden">
      <div className="orb orb-orange" style={{width: 500, height: 500, top: -200, right: -100, opacity: 0.25}}/>
      <div className="relative w-full max-w-md card-smart p-8 md:p-10" data-testid="reset-card">
        <Link to="/" className="inline-flex mb-6"><LogoMark size={64} pulse/></Link>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF7A29] mb-3">
          <KeyRound className="w-4 h-4"/> Imposta nuova password
        </div>
        <h1 className="font-display text-3xl mb-2">Reimposta password</h1>
        <p className="text-sm text-[#757575] mb-6">Inserisci il codice di 6 cifre ricevuto via email e scegli una nuova password.</p>

        <form onSubmit={submit} className="space-y-4">
          {!params.get("email") && (
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="reset-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="rounded-xl mt-1 h-11"/>
            </div>
          )}
          <div>
            <Label htmlFor="code">Codice (6 cifre)</Label>
            <Input
              id="code"
              data-testid="reset-code"
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              value={code}
              onChange={(e)=>setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className="rounded-xl mt-1 h-12 text-center font-mono text-2xl tracking-[0.4em] font-bold"
            />
          </div>
          <div>
            <Label htmlFor="pwd">Nuova password</Label>
            <div className="relative mt-1">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#757575]"/>
              <Input id="pwd" data-testid="reset-pwd" type={showPwd ? "text" : "password"} required minLength={6} value={pwd} onChange={(e)=>setPwd(e.target.value)} className="rounded-xl pl-10 pr-10 h-11"/>
              <button type="button" onClick={()=>setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#757575]" aria-label="mostra password">
                {showPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="pwd2">Conferma password</Label>
            <div className="relative mt-1">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#757575]"/>
              <Input id="pwd2" data-testid="reset-pwd2" type={showPwd ? "text" : "password"} required minLength={6} value={pwd2} onChange={(e)=>setPwd2(e.target.value)} className="rounded-xl pl-10 h-11"/>
            </div>
            {pwd && pwd2 && pwd !== pwd2 && <div className="text-xs text-red-600 mt-1">Le password non coincidono</div>}
          </div>
          <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full rounded-full h-12 bg-[#FF7A29] hover:bg-[#E85D00] btn-glow shadow-md shadow-orange-200">
            {loading ? "Reset…" : "Reimposta password"}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-[#EDE5D8] flex items-center justify-between">
          <Link to="/login" className="text-xs text-[#757575] hover:text-[#FF7A29]">← Torna al login</Link>
          <button type="button" onClick={resend} data-testid="reset-resend" className="text-xs text-[#FF7A29] hover:underline">Reinvia codice</button>
        </div>
      </div>
    </div>
  );
}
